import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppTopBar } from '../layout/AppShell';
import { parentAuth, ParentApiError, type ParentSessionInfo } from '../../services/parentApi';
import { PARENT_SIGN_IN_PATH } from '../../utils/parentPortal';

/**
 * Parent portal session (#268). Decided by the server (GET /api/parent-auth?action=session),
 * never by a Supabase profile role: families don't have Supabase accounts.
 */
type ParentSessionValue = { session: ParentSessionInfo; signOut: () => Promise<void> };
const ParentSessionContext = createContext<ParentSessionValue | null>(null);

export const useParentSession = () => {
  const value = useContext(ParentSessionContext);
  if (!value) throw new Error('useParentSession must be used inside ParentRoute');
  return value;
};

/** Sends the visitor to sign-in, remembering where they were going. */
export const signInRedirect = (pathname: string) =>
  `${PARENT_SIGN_IN_PATH}?next=${encodeURIComponent(pathname)}`;

export const ParentRoute = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'signed-out' | 'error' | ParentSessionInfo>('loading');

  const check = useCallback(() => {
    setState('loading');
    parentAuth.session()
      .then(setState)
      .catch((err: unknown) => setState(err instanceof ParentApiError && err.status === 401 ? 'signed-out' : 'error'));
  }, []);

  useEffect(check, [check]);

  const signOut = useCallback(async () => {
    try { await parentAuth.logout(); } catch { /* the cookie is cleared server-side either way */ }
    navigate(PARENT_SIGN_IN_PATH, { replace: true });
  }, [navigate]);

  if (state === 'loading') {
    return (
      <div className="flex w-full min-h-screen justify-center items-center text-gray-500" role="status">
        Loading…
      </div>
    );
  }
  if (state === 'signed-out') return <Navigate to={signInRedirect(location.pathname)} replace />;
  if (state === 'error') {
    return (
      <div className="flex w-full min-h-screen flex-col justify-center items-center gap-4 px-4 text-center">
        <p className="text-gray-700">We couldn't check your sign-in just now.</p>
        <button onClick={check} className="min-h-[48px] px-4 py-2 rounded-md bg-blue-600 text-white text-base font-medium hover:bg-blue-700">
          Try again
        </button>
      </div>
    );
  }
  return <ParentSessionContext.Provider value={{ session: state, signOut }}>{children}</ParentSessionContext.Provider>;
};

/** Simple header for families: school, who is signed in, and sign out. */
/**
 * The parent portal's top bar: the slim variant of the Sprout app shell (#358).
 * Presentational only; the session and sign-out are unchanged.
 */
export const ParentHeader = () => {
  const { session, signOut } = useParentSession();
  return <AppTopBar title="Learning Diversity documents" account={session.email} onSignOut={signOut} />;
};
